'use client';

import * as React from 'react';
import { DateRange } from 'react-day-picker';

import { Calendar } from '../../../common/calendar/Calendar';
import { ResponsiveDialog } from '../../../common/dialog/responsiveDialog';
import { Button } from '../../../ui/button';

// Default empty translations object
const DEFAULT_TRANSLATIONS: DateRangeDialogProps['translations'] = {};

interface DateRangeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialDateRange?: DateRange;
  onConfirm: (dateRange: DateRange) => void;
  onReset?: () => void;
  showReset?: boolean;
  title?: string;
  /**
   * Maximum number of days the range can span. When omitted, no cap is
   * enforced and the "maximum range" helper text is not shown. Set this
   * explicitly when the feature requires bounding the selection (e.g. search
   * filters that only query a rolling window).
   */
  maxDays?: number;
  /**
   * When true, the user can confirm after picking a single day (the second
   * click is optional). In that case `onConfirm` is called with
   * `{ from, to: from }` — i.e. a zero-width range representing a single-day
   * filter. Defaults to false to preserve the original range-only behavior.
   */
  allowSingleDay?: boolean;
  /** Dialog width. Defaults to `default` to preserve existing layouts. */
  size?: 'sm' | 'default' | 'lg' | 'xl';
  circularDays?: boolean;
  fullWidth?: boolean;
  separateYearMonthNav?: boolean;
  shortenMonthNames?: boolean;
  locale?: string;
  translations?: {
    reset?: string;
    cancel?: string;
    apply?: string;
    maxDaysWarning?: string;
  };
}

export const DateRangeDialog = ({
  open,
  onOpenChange,
  initialDateRange,
  onConfirm,
  onReset,
  showReset = false,
  title = 'Custom Duration',
  maxDays,
  allowSingleDay = false,
  size = 'default',
  circularDays = false,
  fullWidth = false,
  separateYearMonthNav = false,
  shortenMonthNames = false,
  locale = 'en',
  translations = DEFAULT_TRANSLATIONS
}: DateRangeDialogProps) => {
  const [tempDateRange, setTempDateRange] = React.useState<DateRange | undefined>(initialDateRange);

  // Calculate year range for dropdown (10 years back, 2 years forward from current year)
  const yearRange = React.useMemo(() => {
    const currentYear = new Date().getFullYear();
    return {
      fromYear: currentYear - 10,
      toYear: currentYear + 2
    };
  }, []);

  // Compute the latest selectable end date based on the selected start +
  // maxDays. Skipped entirely when maxDays is not set — no cap, no disabled
  // days.
  const maxEndDate = React.useMemo(() => {
    if (!tempDateRange?.from || maxDays === undefined) return undefined;
    const max = new Date(tempDateRange.from);
    max.setDate(max.getDate() + maxDays);
    return max;
  }, [tempDateRange?.from, maxDays]);

  const disabledDays = React.useMemo(() => {
    if (!maxEndDate) return undefined;
    return { after: maxEndDate };
  }, [maxEndDate]);

  // Update temp range when initial range changes
  React.useEffect(() => {
    setTempDateRange(initialDateRange);
  }, [initialDateRange, open]);

  const handleConfirm = () => {
    if (!tempDateRange?.from) return;
    if (tempDateRange.to) {
      onConfirm(tempDateRange);
      onOpenChange(false);
      return;
    }
    if (allowSingleDay) {
      // User picked only a start day — treat it as a single-day filter.
      onConfirm({ from: tempDateRange.from, to: tempDateRange.from });
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    setTempDateRange(initialDateRange);
    onOpenChange(false);
  };

  const handleReset = () => {
    setTempDateRange(undefined);
    onReset?.();
    onOpenChange(false);
  };

  const showMaxDaysWarning = maxDays !== undefined;
  const maxDaysWarning = translations.maxDaysWarning || `Maximum range: ${maxDays} days`;

  return (
    <ResponsiveDialog isOpen={open} onClose={handleCancel} title={title} size={size} mobileSide="bottom">
      <div className="py-4">
        <Calendar
          mode="range"
          selected={tempDateRange}
          onSelect={setTempDateRange}
          disabled={disabledDays}
          numberOfMonths={1}
          circularDays={circularDays}
          separateYearMonthNav={separateYearMonthNav}
          shortenMonthNames={shortenMonthNames}
          yearRange={separateYearMonthNav ? yearRange : undefined}
          locale={locale}
          className={fullWidth ? 'w-full' : ''}
          classNames={
            fullWidth
              ? {
                  months: 'flex flex-col sm:flex-row gap-2 w-full',
                  month: 'flex flex-col gap-4 w-full items-center',
                  caption: 'flex justify-center pt-1 relative items-center w-full',
                  caption_label: 'text-sm font-medium',
                  nav: 'flex items-center gap-1',
                  nav_button_previous: 'absolute left-1',
                  nav_button_next: 'absolute right-1',
                  table: 'w-full border-collapse',
                  head_row: 'flex w-full justify-between',
                  head_cell: 'text-center',
                  row: 'flex w-full mt-2 justify-between',
                  cell: 'flex items-center justify-center',
                  day: 'min-w-[40px] min-h-[40px]'
                }
              : undefined
          }
        />
        {showMaxDaysWarning && tempDateRange?.from && (
          <p className="text-xs text-neutral-500 text-center mt-2">{maxDaysWarning}</p>
        )}
      </div>
      <div className={`flex gap-2 ${showReset ? 'justify-between' : 'justify-end'} w-full flex-col md:flex-row mt-4`}>
        {showReset && (
          <Button variant="outline" onClick={handleReset} className="w-full md:w-auto">
            {translations.reset || 'Reset'}
          </Button>
        )}
        <div className="flex gap-2 flex-col md:flex-row w-full md:w-auto">
          <Button variant="outline" onClick={handleCancel} className="w-full md:w-auto">
            {translations.cancel || 'Cancel'}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!tempDateRange?.from || (!allowSingleDay && !tempDateRange?.to)}
            className="w-full md:w-auto"
          >
            {translations.apply || 'Apply'}
          </Button>
        </div>
      </div>
    </ResponsiveDialog>
  );
};
